/**
 * AnalyticsPanel.jsx — HealHub Admin Analytics Dashboard
 * ────────────────────────────────────────────────────────
 * Updated to match the new analyticsService.js subcollection pattern:
 *
 *   DAU  = count(analytics_daily/{day}/dau/)
 *   MAU  = count(analytics_daily/month_{month}/mau/)
 *
 * The old dau_count / mau_count fields on the day doc are gone.
 * new_users is now derived by counting docs where is_new == true in the dau subcollection.
 */

import { useState, useEffect } from "react";
import {
  collection, doc, getDoc, getCountFromServer,
  query, where, addDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";

// ─── colour palette ───────────────────────────────────────────────────────────
const C = {
  bg: "#f4f5fb", card: "#ffffff", border: "#e8eaf6", ink: "#1a1a2e",
  ink2: "#3a3a5e", muted: "#888",
  blue: "#3949ab", blueL: "#e8eaf6",
  green: "#2e7d32", greenL: "#e8f5e9",
  red: "#c62828", redL: "#ffebee",
  amber: "#e65100", amberL: "#fff3e0",
  teal: "#00695c", tealL: "#e0f2f1",
  purple: "#6a1b9a", purpleL: "#f3e5f5",
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const todayKey  = () => new Date().toISOString().slice(0, 10);
const monthKey  = () => new Date().toISOString().slice(0, 7);
const daysAgo   = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const monthsAgo = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d.toISOString().slice(0, 7); };
const fmtDate   = (iso) => { const [, m, d] = iso.split("-"); return `${parseInt(d)} ${["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m)]}`; };
const fmtMonth  = (iso) => { const [y, m] = iso.split("-"); return `${["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m)]} ${y.slice(2)}`; };
const pct       = (a, b) => (!b ? 0 : Math.round((a / b) * 100));
const growth    = (now, prev) => (!prev ? (now > 0 ? 100 : 0) : Math.round(((now - prev) / prev) * 100));

/**
 * getDAU — counts docs in analytics_daily/{day}/dau subcollection.
 * Falls back to 0 on permission error (shouldn't happen for admins).
 */
async function getDAU(day) {
  try {
    const snap = await getCountFromServer(collection(db, "analytics_daily", day, "dau"));
    return snap.data().count;
  } catch { return 0; }
}

/**
 * getMAU — counts docs in analytics_daily/month_{month}/mau subcollection.
 */
async function getMAU(month) {
  try {
    const snap = await getCountFromServer(collection(db, "analytics_daily", `month_${month}`, "mau"));
    return snap.data().count;
  } catch { return 0; }
}

/**
 * getNewUsers — counts dau docs where is_new == true.
 */
async function getNewUsers(day) {
  try {
    const q    = query(collection(db, "analytics_daily", day, "dau"), where("is_new", "==", true));
    const snap = await getCountFromServer(q);
    return snap.data().count;
  } catch { return 0; }
}

// ─── UI sub-components ────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color = C.blue, colorL = C.blueL, loading }) {
  return (
    <div style={{
      background: C.card, borderRadius: 16, padding: "20px 22px",
      border: `1px solid ${C.border}`, boxShadow: "0 2px 12px rgba(57,73,171,.07)",
      display: "flex", flexDirection: "column", gap: 10, flex: 1, minWidth: 160,
    }}>
      <div style={{ width: 42, height: 42, borderRadius: 12, background: colorL, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{icon}</div>
      {loading
        ? <div style={{ height: 32, width: "60%", borderRadius: 8, background: C.border, animation: "adm-shimmer 1.4s infinite" }} />
        : <div style={{ fontSize: 28, fontWeight: 800, color: C.ink, lineHeight: 1, letterSpacing: "-.02em" }}>{value?.toLocaleString?.() ?? value}</div>
      }
      <div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color, fontWeight: 600, marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ChartCard({ title, sub, children, height = 200 }) {
  return (
    <div style={{
      background: C.card, borderRadius: 16, padding: "20px 22px",
      border: `1px solid ${C.border}`, boxShadow: "0 2px 12px rgba(57,73,171,.07)",
      marginBottom: 16,
    }}>
      <SectionHeader title={title} sub={sub} />
      <div style={{ height }}>{children}</div>
    </div>
  );
}

function MiniMetric({ label, value, color, pctVal }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
        <span style={{ fontSize: 13, color: C.ink2 }}>{label}</span>
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{value?.toLocaleString()}</span>
        {pctVal !== undefined && (
          <span style={{ fontSize: 11, fontWeight: 600, color: pctVal >= 0 ? C.green : C.red }}>
            {pctVal >= 0 ? "+" : ""}{pctVal}%
          </span>
        )}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function AnalyticsPanel() {
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [totalUsers,    setTotalUsers]    = useState(0);
  const [todayData,     setTodayData]     = useState({});   // chattr_sessions, login_events, chattr_live
  const [todayDAU,      setTodayDAU]      = useState(0);
  const [todayNew,      setTodayNew]      = useState(0);
  const [dauSeries,     setDauSeries]     = useState([]);
  const [mauSeries,     setMauSeries]     = useState([]);
  const [chattrSeries,  setChattrSeries]  = useState([]);
  const [newUserSeries, setNewUserSeries] = useState([]);
  const [liveChattr,    setLiveChattr]    = useState(0);
  const [healthScore,   setHealthScore]   = useState(0);
  const [weekGrowth,    setWeekGrowth]    = useState(0);
  const [refreshedAt,   setRefreshedAt]   = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true); setError(null);
    try {
      await Promise.all([fetchTotalUsers(), fetchDailySeries(), fetchMonthlySeries()]);
      setRefreshedAt(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }));
    } catch (e) {
      setError("Failed to load analytics. Check Firestore rules.");
      console.error("[AnalyticsPanel]", e);
    } finally {
      setLoading(false);
    }
  };

  // ── total registered users ──────────────────────────────────────────────────
  const fetchTotalUsers = async () => {
    try {
      const snap = await getCountFromServer(collection(db, "users"));
      setTotalUsers(snap.data().count);
    } catch { setTotalUsers("—"); }
  };

  // ── 30-day daily series ─────────────────────────────────────────────────────
  const fetchDailySeries = async () => {
    const days = Array.from({ length: 30 }, (_, i) => daysAgo(29 - i));

    // Fetch event-counter fields (chattr_sessions, login_events, chattr_live)
    // and DAU + new_user counts in parallel for all 30 days.
    const [daySnaps, dauCounts, newCounts] = await Promise.all([
      Promise.all(days.map(d => getDoc(doc(db, "analytics_daily", d)))),
      Promise.all(days.map(d => getDAU(d))),
      Promise.all(days.map(d => getNewUsers(d))),
    ]);

    const dau      = [];
    const newUsers = [];
    const chattr   = [];

    days.forEach((day, i) => {
      const d = daySnaps[i].exists() ? daySnaps[i].data() : {};
      dau.push     ({ date: day, label: fmtDate(day), value: dauCounts[i] });
      newUsers.push({ date: day, label: fmtDate(day), value: newCounts[i] });
      chattr.push  ({ date: day, label: fmtDate(day), value: d.chattr_sessions || 0 });
    });

    setDauSeries(dau);
    setNewUserSeries(newUsers);
    setChattrSeries(chattr);

    // today's scalar values
    const todaySnap = daySnaps[daySnaps.length - 1];
    const todayDoc  = todaySnap.exists() ? todaySnap.data() : {};
    setTodayData(todayDoc);
    setTodayDAU(dauCounts[dauCounts.length - 1]);
    setTodayNew(newCounts[newCounts.length - 1]);
    setLiveChattr(Math.max(0, todayDoc.chattr_live || 0));

    // health score — DAU/MAU ratio
    const mau = await getMAU(monthKey());
    setHealthScore(mau > 0 ? Math.min(100, Math.round((dauCounts[dauCounts.length - 1] / mau) * 100)) : 0);

    // 7-day growth
    const last7 = dau.slice(-7).reduce((s, x) => s + x.value, 0);
    const prev7 = dau.slice(-14, -7).reduce((s, x) => s + x.value, 0);
    setWeekGrowth(growth(last7, prev7));
  };

  // ── 6-month MAU series ──────────────────────────────────────────────────────
  const fetchMonthlySeries = async () => {
    const months = Array.from({ length: 6 }, (_, i) => monthsAgo(5 - i));
    const counts = await Promise.all(months.map(m => getMAU(m)));
    setMauSeries(months.map((m, i) => ({ month: m, label: fmtMonth(m), value: counts[i] })));
  };

  // ── derived ──────────────────────────────────────────────────────────────────
  const thisMonthMAU   = mauSeries[mauSeries.length - 1]?.value || 0;
  const totalChattr30  = chattrSeries.reduce((s, x) => s + x.value, 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 14px", boxShadow: "0 4px 16px rgba(57,73,171,.12)", fontSize: 13 }}>
        <div style={{ fontWeight: 700, color: C.ink, marginBottom: 4 }}>{label}</div>
        {payload.map((p, i) => <div key={i} style={{ color: p.color, fontWeight: 600 }}>{p.name}: {p.value?.toLocaleString()}</div>)}
      </div>
    );
  };

  if (error) return (
    <div style={{ padding: 24, color: C.red, background: C.redL, borderRadius: 12, margin: 20 }}>⚠ {error}</div>
  );

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", color: C.ink }}>
      <style>{`@keyframes adm-shimmer { 0%{opacity:.4} 50%{opacity:1} 100%{opacity:.4} }`}</style>

      {/* TOP BAR */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.ink, letterSpacing: "-.01em" }}>📈 Platform Analytics</div>
          {refreshedAt && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Last refreshed at {refreshedAt}</div>}
        </div>
        <button onClick={fetchAll} disabled={loading} style={{
          padding: "8px 16px", background: C.blueL, border: `1px solid ${C.blue}40`,
          borderRadius: 9, color: C.blue, fontSize: 13, fontWeight: 700,
          cursor: loading ? "not-allowed" : "pointer", opacity: loading ? .6 : 1, fontFamily: "inherit",
        }}>
          {loading ? "Loading…" : "↻ Refresh"}
        </button>
      </div>

      {/* HERO CARDS */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 24 }}>
        <StatCard icon="👥" label="Total Registered" value={totalUsers}     sub={`+${todayNew} today`}                color={C.blue}   colorL={C.blueL}   loading={loading} />
        <StatCard icon="🔥" label="Daily Active (DAU)"  value={todayDAU}    sub={`${pct(todayDAU, totalUsers)}% of total`} color={C.amber}  colorL={C.amberL}  loading={loading} />
        <StatCard icon="📅" label="Monthly Active (MAU)" value={thisMonthMAU} sub={`Health: ${healthScore}% DAU/MAU`}  color={C.green}  colorL={C.greenL}  loading={loading} />
        <StatCard icon="💬" label="Live on Chattr"     value={liveChattr}   sub={`${totalChattr30} sessions (30d)`}   color={C.teal}   colorL={C.tealL}   loading={loading} />
      </div>

      {/* HEALTH + GROWTH ROW */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 16 }}>

        {/* Platform health */}
        <div style={{ background: C.card, borderRadius: 16, padding: "20px 22px", border: `1px solid ${C.border}`, boxShadow: "0 2px 12px rgba(57,73,171,.07)", flex: 1, minWidth: 220 }}>
          <SectionHeader title="Platform Health Score" sub="DAU ÷ MAU ratio — higher = stickier" />
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              width: 80, height: 80, borderRadius: "50%",
              background: `conic-gradient(${healthScore >= 20 ? C.green : healthScore >= 10 ? C.amber : C.red} ${healthScore * 3.6}deg, ${C.border} 0)`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: C.card, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: C.ink }}>
                {loading ? "…" : `${healthScore}%`}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4, color: healthScore >= 20 ? C.green : healthScore >= 10 ? C.amber : C.red }}>
                {healthScore >= 20 ? "🟢 Excellent" : healthScore >= 10 ? "🟡 Good" : healthScore >= 5 ? "🟠 Growing" : "🔵 Early Stage"}
              </div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                Industry avg: 10–20%<br />{todayDAU} active today of {thisMonthMAU} monthly
              </div>
            </div>
          </div>
        </div>

        {/* 7-day growth */}
        <div style={{ background: C.card, borderRadius: 16, padding: "20px 22px", border: `1px solid ${C.border}`, boxShadow: "0 2px 12px rgba(57,73,171,.07)", flex: 1, minWidth: 220 }}>
          <SectionHeader title="7-Day User Growth" sub="This week vs prior week (DAU total)" />
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
            <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: "-.02em", color: weekGrowth >= 0 ? C.green : C.red }}>
              {loading ? "…" : `${weekGrowth >= 0 ? "+" : ""}${weekGrowth}%`}
            </div>
            <div style={{ fontSize: 14, color: C.muted }}>vs last 7 days</div>
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>
            This week: {dauSeries.slice(-7).reduce((s, x) => s + x.value, 0).toLocaleString()} DAU total<br />
            Last week: {dauSeries.slice(-14, -7).reduce((s, x) => s + x.value, 0).toLocaleString()} DAU total
          </div>
        </div>

        {/* Today at a glance */}
        <div style={{ background: C.card, borderRadius: 16, padding: "20px 22px", border: `1px solid ${C.border}`, boxShadow: "0 2px 12px rgba(57,73,171,.07)", flex: 1, minWidth: 220 }}>
          <SectionHeader title="Today at a Glance" sub={new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })} />
          <MiniMetric label="Active Users"        value={todayDAU}                          color={C.amber}  pctVal={weekGrowth} />
          <MiniMetric label="New Registrations"   value={todayNew}                          color={C.blue} />
          <MiniMetric label="Chattr Sessions"     value={todayData?.chattr_sessions || 0}   color={C.teal} />
          <MiniMetric label="Login Events"        value={todayData?.login_events    || 0}   color={C.purple} />
        </div>
      </div>

      {/* 30-DAY DAU */}
      <ChartCard title="Daily Active Users — Last 30 Days" sub="Unique users who logged in each day" height={220}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={dauSeries} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="dauGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={C.blue} stopOpacity={0.18} />
                <stop offset="95%" stopColor={C.blue} stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} interval={4} />
            <YAxis tick={{ fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="value" name="DAU" stroke={C.blue} strokeWidth={2.5} fill="url(#dauGrad)" dot={false} activeDot={{ r: 5, fill: C.blue, strokeWidth: 0 }} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* NEW REGISTRATIONS */}
      <ChartCard title="New Registrations — Last 30 Days" sub="Users who created an account each day" height={200}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={newUserSeries} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} interval={4} />
            <YAxis tick={{ fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" name="New Users" fill={C.blue} radius={[5, 5, 0, 0]} maxBarSize={22} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* MAU 6 MONTHS */}
      <ChartCard title="Monthly Active Users — Last 6 Months" sub="Unique users who logged in each month" height={200}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={mauSeries} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.muted }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" name="MAU" fill={C.green} radius={[6, 6, 0, 0]} maxBarSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* CHATTR SESSIONS */}
      <ChartCard title="Chattr Sessions — Last 30 Days" sub="Anonymous peer chat sessions initiated each day" height={200}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chattrSeries} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="chattrGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={C.teal} stopOpacity={0.2} />
                <stop offset="95%" stopColor={C.teal} stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} interval={4} />
            <YAxis tick={{ fontSize: 10, fill: C.muted }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="value" name="Sessions" stroke={C.teal} strokeWidth={2.5} fill="url(#chattrGrad)" dot={false} activeDot={{ r: 5, fill: C.teal, strokeWidth: 0 }} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* MONTHLY TABLE */}
      <div style={{ background: C.card, borderRadius: 16, padding: "20px 22px", border: `1px solid ${C.border}`, boxShadow: "0 2px 12px rgba(57,73,171,.07)", marginBottom: 16 }}>
        <SectionHeader title="Monthly MAU Summary" sub="User activity breakdown per month" />
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 400 }}>
            <thead>
              <tr style={{ background: "#f8f9ff" }}>
                {["Month", "MAU", "vs Prev", "Est. DAU avg", "Growth"].map(h => (
                  <th key={h} style={{ padding: "9px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em", borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mauSeries.map((row, i) => {
                const prev = i > 0 ? mauSeries[i - 1].value : null;
                const g    = prev !== null ? growth(row.value, prev) : null;
                const estDAU = row.value > 0 ? Math.round(row.value * 0.1) : 0;
                return (
                  <tr key={row.month} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: "10px 14px", fontWeight: 600, color: C.ink }}>{row.label}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 700, color: C.ink }}>{row.value.toLocaleString()}</td>
                    <td style={{ padding: "10px 14px", color: C.muted }}>{prev !== null ? prev.toLocaleString() : "—"}</td>
                    <td style={{ padding: "10px 14px", color: C.muted }}>{estDAU > 0 ? `~${estDAU}` : "—"}</td>
                    <td style={{ padding: "10px 14px" }}>
                      {g !== null
                        ? <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: g >= 0 ? C.greenL : C.redL, color: g >= 0 ? C.green : C.red }}>{g >= 0 ? "+" : ""}{g}%</span>
                        : <span style={{ color: C.muted }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ fontSize: 11, color: C.muted, textAlign: "center", padding: "12px 0 4px", lineHeight: 1.7 }}>
        DAU/MAU counts are derived from subcollection doc counts — accurate, deduplicated, and cache-safe.<br />
        Chattr live count resets on page reload.
      </div>
    </div>
  );
}